import { BaseMonitor } from '@exodus/asset-lib'
import { SynchronizedTime } from '@exodus/basic-utils'
import { Tx } from '@exodus/models'
import lodash from 'lodash'

import { fromHexToString } from '../number-utils.js'
import { UNCONFIRMED_TX_LIMIT } from './monitor-utils/get-derive-transactions-to-check.js'
import {
  excludeUnchangedTokenBalances,
  getDeriveDataNeededForTick,
  getDeriveTransactionsToCheck,
} from './monitor-utils/index.js'

const { isEmpty, unionBy, zipObject } = lodash

// The base ethereum monitor no history class handles listening for assets with no history

export class EthereumNoHistoryMonitor extends BaseMonitor {
  constructor({ server, config, ...args }) {
    super(args)
    this.server = server
    this.config = { ...config }
    this.deriveDataNeededForTick = getDeriveDataNeededForTick(this.aci)
    this.deriveTransactionsToCheck = getDeriveTransactionsToCheck({
      getTxLog: (...args) => this.aci.getTxLog(...args),
    })
  }

  setServer(config) {
    const uri = config?.server || this.server.defaultUri

    if (uri === this.server.uri) {
      return
    }

    this.server.setURI(uri)
  }

  async getBalances({ tokens, ourWalletAddress }) {
    const batch = Object.create(null)
    const request = this.server.getBalanceRequest(ourWalletAddress)
    batch[this.asset.name] = request
    for (const token of tokens) {
      const request = this.server.balanceOfRequest(ourWalletAddress, token.contract.address)
      batch[token.name] = request
    }

    const pairs = Object.entries(batch)
    if (pairs.length === 0) {
      return {}
    }

    const requests = pairs.map((pair) => pair[1])
    const responses = await this.server.sendBatchRequest(requests)
    const entries = pairs.map((pair, idx) => {
      const balanceHex = responses[idx]
      const name = pair[0]
      const balance = fromHexToString(balanceHex)
      return [name, balance]
    })
    return Object.fromEntries(entries)
  }

  async getNewAccountState({ tokens, currentTokenBalances, ourWalletAddress }) {
    const asset = this.asset
    const newAccountState = {}
    const balances = await this.getBalances({ tokens, ourWalletAddress })
    const balance = balances[asset.name]
    newAccountState.balance = asset.currency.baseUnit(balance)
    const tokenBalancePairs = Object.entries(balances).filter((entry) => entry[0] !== asset.name)
    const tokenBalanceEntries = tokenBalancePairs
      .map((pair) => {
        const token = tokens.find((token) => token.name === pair[0])
        if (!token) return null
        const value = token.currency.baseUnit(pair[1] || 0)
        return [token.name, value]
      })
      .filter(Boolean)

    const tokenBalances = excludeUnchangedTokenBalances(currentTokenBalances, tokenBalanceEntries)
    if (!isEmpty(tokenBalances)) newAccountState.tokenBalances = tokenBalances
    return newAccountState
  }

  async deriveData({ assetSource, tokens }) {
    const { assetName, walletAccount } = assetSource

    const { ourWalletAddress, currentAccountState } = await this.deriveDataNeededForTick({
      assetName,
      walletAccount,
    })

    const { pendingTransactionsGroupedByAddressAndNonce, pendingTransactionsToCheck } =
      await this.deriveTransactionsToCheck({
        assetName,
        walletAccount,
        tokens,
        ourWalletAddress,
      })

    const pendingTransactions = unionBy(
      Object.values(pendingTransactionsGroupedByAddressAndNonce),
      Object.values(pendingTransactionsToCheck),
      'tx.txId'
    )

    return { ourWalletAddress, pendingTransactions, currentAccountState }
  }

  async getTransactionsFromNode(transactions) {
    const batch = []
    for (const { tx } of transactions) {
      const request = this.server.getTransactionByHashRequest(tx.txId)
      batch.push(request)
    }

    try {
      const responses = await this.server.sendBatchRequest(batch)
      const txIds = transactions.map(({ tx }) => tx.txId)
      return zipObject(txIds, responses)
    } catch (error) {
      console.warn({ error })
      return []
    }
  }

  async checkPendingTransactions({ pendingTransactions, pendingTxsFromNode, walletAccount }) {
    const txsToUpdate = []
    const txsToRemove = []
    const now = SynchronizedTime.now()

    if (isEmpty(pendingTransactions))
      return {
        txsToUpdate,
        txsToRemove,
      }

    for (const { tx, assetName } of pendingTransactions) {
      const txFromNode = pendingTxsFromNode[tx.txId]
      const isConfirmed = Boolean(txFromNode?.blockHash)
      if (now - tx.date.getTime() > UNCONFIRMED_TX_LIMIT && !isConfirmed) {
        txsToRemove.push({
          tx,
          assetSource: { asset: assetName, walletAccount },
        })
      } else if (isConfirmed) {
        txsToUpdate.push({
          tx: { ...tx, confirmations: 1 },
          assetSource: { asset: assetName, walletAccount },
        })
      }
    }

    return {
      txsToUpdate,
      txsToRemove,
    }
  }

  async tick({ refresh, walletAccount }) {
    const assets = await this.aci.getAssetsForNetwork({ baseAssetName: this.asset.name })
    const tokens = Object.values(assets).filter((asset) => asset.baseAsset.name !== asset.name)

    const assetSource = { assetName: this.asset.name, walletAccount }

    const { ourWalletAddress, pendingTransactions, currentAccountState } = await this.deriveData({
      assetSource,
      tokens,
    })

    const pendingTxsFromNode = await this.getTransactionsFromNode(pendingTransactions)

    const { txsToUpdate, txsToRemove } = await this.checkPendingTransactions({
      pendingTransactions,
      pendingTxsFromNode,
      walletAccount,
    })

    const logItemsByAsset = Object.create(null)

    txsToUpdate.forEach((txToUpdate) => {
      const assetName = txToUpdate.assetSource.asset
      logItemsByAsset[assetName] = logItemsByAsset[assetName] || []
      logItemsByAsset[assetName].push(Tx.fromJSON(txToUpdate.tx))
    })

    const accountState = await this.getNewAccountState({
      tokens,
      currentTokenBalances: currentAccountState?.tokenBalances,
      ourWalletAddress,
    })

    await this.updateAccountState({ newData: { ...accountState }, walletAccount })

    await this.removeFromTxLog(txsToRemove)
    await this.updateTxLogByAsset({ logItemsByAsset, walletAccount, refresh })
  }
}
